import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { map, take, tap } from "rxjs/operators";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { currentBuildingUnsetAction } from "src/components/shared-components/state/actions/building.action";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { environment } from "src/environments/environment";
import { Voting, VotingStatus } from "src/model/class/general classes/voting";
import { EventService } from "./event.service";

/**
 * Frontend service for the Voting feature. Mirrors DocumentSecurityService:
 * keeps the current area id in sync with the selected building, talks to the
 * QuoteServer `/voting` API and drives the direct-to-S3 attachment upload via
 * presigned URLs. Unlike document security, a voting can hold many files, so
 * the upload flow creates one file row per attachment (the presigned-URL call
 * returns the new file id used to confirm/download/delete it).
 */
@Injectable({
  providedIn: "root",
})
export class VotingService implements OnDestroy {
  private _idArea: number = null;
  private _eventSubscriptions: Array<Subscription> = [];

  constructor(
    private _http: HttpClient,
    private _store: Store,
    private _eventService: EventService,
  ) {
    this.subscribeEvents();
  }

  ngOnDestroy(): void {
    this.unsubscribeEvents();
  }

  private get _baseUrl(): string {
    return `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_VOTING}`;
  }

  /**
   * Fetch all votings for the current area. Resolves to the HTTP status code
   * (e.g. 404) instead of a list when no area is set or the call is non-200.
   */
  public getVotings(): Promise<Array<Voting> | number> {
    if (!this._idArea) return Promise.resolve(404);

    return this._http
      .get<Array<Voting>>(`${this._baseUrl}?idArea=${this._idArea}`, {
        observe: "response",
      })
      .pipe(
        map((responseData: HttpResponse<Object>) => {
          if (responseData.status !== 200) {
            return responseData.status;
          }
          return (responseData.body || []) as Array<Voting>;
        }),
        take(1),
      )
      .toPromise();
  }

  /**
   * Create a new voting (with its options). The area is attached defensively to
   * cover the first create right after entering the page.
   */
  public async createVoting(payload: Partial<Voting>): Promise<Voting> {
    const idArea = await this.resolveIdArea();

    return this._http
      .post<Voting>(this._baseUrl, {
        idArea,
        title: payload.title,
        description: payload.description || "",
        dateFrom: payload.dateFrom,
        dateTo: payload.dateTo,
        multipleChoice: !!payload.multipleChoice,
        options: payload.options || [],
      })
      .pipe(take(1))
      .toPromise();
  }

  /**
   * Update a voting (allowed server-side only while it is DRAFT).
   */
  public updateVoting(id: number, payload: Partial<Voting>): Promise<Voting> {
    return this._http
      .patch<Voting>(`${this._baseUrl}/${id}`, {
        title: payload.title,
        description: payload.description || "",
        dateFrom: payload.dateFrom,
        dateTo: payload.dateTo,
        multipleChoice: !!payload.multipleChoice,
        options: payload.options || [],
      })
      .pipe(take(1))
      .toPromise();
  }

  /**
   * Delete a voting (any state) and its cascade (options/votes/files).
   */
  public deleteVoting(id: number): Promise<boolean> {
    return this._http
      .delete(`${this._baseUrl}/${id}`, { observe: "response" })
      .pipe(
        map((responseData: HttpResponse<Object>) => responseData.ok),
        take(1),
      )
      .toPromise();
  }

  /**
   * Cast or change the current user's vote (allowed server-side only while the
   * voting is IN_PROGRESS).
   */
  public castVote(id: number, optionIds: Array<number>): Promise<boolean> {
    return this._http
      .post(`${this._baseUrl}/${id}/vote`, { optionIds }, { observe: "response" })
      .pipe(
        map((responseData: HttpResponse<Object>) => responseData.ok),
        take(1),
      )
      .toPromise();
  }

  /**
   * Fetch a single voting with its results (percentages gated server-side by
   * role/state).
   */
  public getResults(id: number): Promise<Voting> {
    return this._http
      .get<Voting>(`${this._baseUrl}/${id}/results`)
      .pipe(take(1))
      .toPromise();
  }

  /**
   * Lightweight area-level check feeding the dashboard alert badge / entry
   * popup: whether the resident has an open voting still to vote. Resolves to a
   * safe default when no area is set.
   */
  public getVotingStatus(): Promise<VotingStatus> {
    return this.getVotingStatusForArea(this._idArea);
  }

  /**
   * Same lightweight pending-vote check as getVotingStatus, but for an explicit
   * area instead of the currently selected one. Lets the building list probe
   * every condominium up-front (before one is selected) to surface pending
   * votes across all of them.
   */
  public getVotingStatusForArea(idArea: number): Promise<VotingStatus> {
    if (!idArea) {
      return Promise.resolve({ hasPendingVote: false });
    }

    return this._http
      .get<VotingStatus>(`${this._baseUrl}/status?idArea=${idArea}`)
      .pipe(
        map((response: VotingStatus) => response),
        take(1),
      )
      .toPromise();
  }

  /**
   * Step 1 of an attachment upload: create the file row server-side and get a
   * presigned PUT URL plus the new file id (used to confirm/download/delete).
   */
  public getUploadUrl(
    votingId: number,
    namefile: string,
  ): Promise<{ url: string; idFile: number }> {
    return this._http
      .post<{ url: string; idFile: number }>(
        `${this._baseUrl}/${votingId}/uploadfile/url?namefile=${encodeURIComponent(
          namefile,
        )}`,
        {},
      )
      .pipe(take(1))
      .toPromise();
  }

  /**
   * Step 2 of an attachment upload: PUT the raw file straight to S3 with the
   * presigned URL. Uses native fetch() to bypass the auth interceptor, which
   * would otherwise poison the SigV4-signed request and leak the session token.
   */
  public async uploadFileToPresignedUrl(
    url: string,
    file: File,
  ): Promise<void> {
    const response = await fetch(url, { method: "PUT", body: file });
    if (!response.ok) {
      throw new Error(`S3 upload failed with status ${response.status}`);
    }
  }

  /**
   * Step 3 of an attachment upload: bind the uploaded object to its file row.
   */
  public confirmUpload(idFile: number, namefile: string): Promise<boolean> {
    return this._http
      .put(
        `${this._baseUrl}/uploadfile/${idFile}?namefile=${encodeURIComponent(
          namefile,
        )}`,
        {},
        { observe: "response" },
      )
      .pipe(
        map((responseData: HttpResponse<Object>) => responseData.ok),
        take(1),
      )
      .toPromise();
  }

  /**
   * Retrieve an attachment, returned by the backend as a serialized buffer
   * ({ data, namefile }) for the caller to rebuild into a blob.
   */
  public getUploadedFile(idFile: number): Promise<any> {
    return this._http
      .get(`${this._baseUrl}/file/${idFile}`)
      .pipe(
        map((responseData: any) => responseData),
        take(1),
      )
      .toPromise();
  }

  /**
   * Delete a single attachment (soft-delete + S3 cleanup server-side).
   */
  public deleteFile(idFile: number): Promise<boolean> {
    return this._http
      .delete(`${this._baseUrl}/file/${idFile}`, { observe: "response" })
      .pipe(
        map((responseData: HttpResponse<Object>) => responseData.ok),
        take(1),
      )
      .toPromise();
  }

  /**
   * Resolve the current area id, falling back to a fresh store read on the very
   * first create after entering the page (subscription not yet emitted).
   */
  private async resolveIdArea(): Promise<number> {
    if (this._idArea && this._idArea > 0) {
      return this._idArea;
    }

    const building = await this._store
      .select(selectedBuilding)
      .pipe(take(1))
      .toPromise();

    return building?.idAreaLocal;
  }

  /**
   * Keep `_idArea` in sync with the selected building, and clear the current
   * building on logout so stale area state is not reused.
   */
  private subscribeEvents(): void {
    this._eventSubscriptions = [];

    this._eventSubscriptions.push(
      this._store
        .select(selectedBuilding)
        .pipe(
          map((building: Building) => building?.idAreaLocal),
          tap((idArea) => {
            this._idArea = idArea;
          }),
        )
        .subscribe(),
    );

    this._eventSubscriptions.push(
      this._eventService?.OnMakeLogout.subscribe(() => {
        this._store.dispatch(currentBuildingUnsetAction());
      }),
    );
  }

  private unsubscribeEvents(): void {
    this._eventSubscriptions.forEach((subscription) =>
      subscription?.unsubscribe(),
    );
  }
}
